import React, { useState, useEffect } from "react";
import { Table, Button, Card, Badge, Modal, Form, Row, Col, InputGroup, Alert, Pagination } from "react-bootstrap";
import { FaPlus, FaKey, FaGlobe, FaEdit, FaTrash, FaCheck, FaEye, FaEyeSlash, FaCopy } from "react-icons/fa";
import { message } from "antd";
import {
  useGetApplicationsQuery,
  useRegisterApplicationMutation,
  useUpdateApplicationMutation,
  useDeleteApplicationMutation
} from "../../../redux/api/AIPlatformApi/aiPlatformApi";
import DeleteModel from "../../../Components/DeleteModel";
import LoaderSpinner from "../../../Components/LoaderSpinner";

const AIApplications = () => {
  // Query states
  const [filters, setFilters] = useState({ page: 1, limit: 10, search: "", status: "", feature: "" });
  const { data: serverData, isLoading, error: fetchError, refetch } = useGetApplicationsQuery(filters);
  const [registerApp, { isLoading: isRegistering }] = useRegisterApplicationMutation();
  const [updateApp, { isLoading: isUpdating }] = useUpdateApplicationMutation();
  const [deleteApp] = useDeleteApplicationMutation();

  // Local state for demo mode fallback
  const [localApps, setLocalApps] = useState([]);
  const [isDemoMode, setIsDemoMode] = useState(false);

  // Modal states
  const [showFormModal, setShowFormModal] = useState(false);
  const [currentApp, setCurrentApp] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [appToDelete, setAppToDelete] = useState(null);
  const [visibleKeys, setVisibleKeys] = useState({});

  // Form states
  const [formData, setFormData] = useState({
    name: "",
    dailyLimit: 100000,
    allowedDomains: "",
    enabledFeatures: [],
    status: "active"
  });

  const availableFeatures = [
    { key: "translate", label: "Translation" },
    { key: "replies", label: "Smart Reply" },
    { key: "sentiment", label: "Sentiment Analysis" },
    { key: "tags", label: "Tag Extraction" },
    { key: "language-detect", label: "Language Detection" },
    { key: "classify", label: "Text Classification" },
    { key: "summarize", label: "Text Summarization" },
    { key: "moderate", label: "Content Moderation" },
    { key: "suggestions", label: "Auto Suggest" },
    { key: "moderate-image", label: "Image Moderate" },
    { key: "ocr", label: "OCR" },
    { key: "stt", label: "Speech To Text" },
    { key: "tts", label: "Text To Speech" },
    { key: "chatbot", label: "Chat Bot" },
  ];


  // Initialize data
  useEffect(() => {
    if (serverData?.data) {
      setLocalApps(serverData.data);
    } 
  }, [serverData, fetchError]);

  const handleOpenFormModal = (app = null) => {
    if (app) {
      setCurrentApp(app);
      setFormData({
        name: app.name,
        dailyLimit: app.dailyLimit,
        allowedDomains: Array.isArray(app.allowedDomains) ? app.allowedDomains.join(", ") : "",
        enabledFeatures: app.enabledFeatures || [],
        status: app.status || "active"
      });
    } else {
      setCurrentApp(null);
      setFormData({
        name: "",
        dailyLimit: 100000,
        allowedDomains: "",
        enabledFeatures: ["translate"],
        status: "active"
      });
    }
    setShowFormModal(true);
  };

  const handleCloseFormModal = () => {
    setShowFormModal(false);
    setCurrentApp(null);
  };

  const handleFeatureToggle = (featureKey) => {
    const updatedFeatures = formData.enabledFeatures.includes(featureKey)
      ? formData.enabledFeatures.filter(f => f !== featureKey)
      : [...formData.enabledFeatures, featureKey];
    setFormData({ ...formData, enabledFeatures: updatedFeatures });
  };

  const handleCopyKey = (key) => {
    navigator.clipboard.writeText(key);
    message.success("API Key copied to clipboard!");
  };

  const toggleKeyVisibility = (id) => {
    setVisibleKeys(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleOpenDeleteModal = (app) => {
    setAppToDelete(app);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setAppToDelete(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const domains = formData.allowedDomains
      .split(",")
      .map(d => d.trim())
      .filter(d => d.length > 0);

    const payload = {
      name: formData.name,
      dailyLimit: Number(formData.dailyLimit),
      allowedDomains: domains,
      enabledFeatures: formData.enabledFeatures,
      status: formData.status
    };

    try {
      if (isDemoMode) {
        // Simulate database CRUD in demo mode
        if (currentApp) {
          setLocalApps(prev => prev.map(a => a._id === currentApp._id ? { ...a, ...payload } : a));
          message.success("Application details updated locally.");
        } else {
          const newApp = {
            _id: `app_${Date.now()}`,
            id: `app_${Date.now()}`,
            ...payload,
            apiKey: `ai_${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`,
            createdAt: new Date().toISOString()
          };
          setLocalApps(prev => [newApp, ...prev]);
          message.success("New application registered locally.");
        }
        handleCloseFormModal();
      } else {
        // Live server API call
        if (currentApp) {
          await updateApp({ id: currentApp._id, ...payload }).unwrap();
          message.success("Application configurations updated successfully.");
        } else {
          await registerApp(payload).unwrap();
          message.success("New application registered successfully.");
        }
        refetch();
        handleCloseFormModal();
      }
    } catch (err) {
      console.error(err);
      message.error(err?.data?.message || err?.message || "Failed to submit application configurations.");
    }
  };

  const handleDeleteConfirm = async () => {
    const targetId = appToDelete._id;
    try {
      if (isDemoMode) {
        setLocalApps(prev => prev.filter(a => a._id !== targetId));
        message.success("Application deleted locally.");
      } else {
        await deleteApp(targetId).unwrap();
        message.success("Application deleted successfully.");
        refetch();
      }
      handleCloseDeleteModal();
    } catch (err) {
      console.error(err);
      message.error("Failed to delete application.");
    }
  };

  if (isLoading && !isDemoMode) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <LoaderSpinner />
      </div>
    );
  }

  // Frontend local search/filtering in demo mode or server data filtering fallback
  const filteredApps = localApps.filter(app => {
    const matchesSearch = app.name?.toLowerCase().includes(filters.search.toLowerCase());
    const matchesStatus = filters.status ? app.status === filters.status : true;
    const matchesFeature = filters.feature ? app.enabledFeatures?.includes(filters.feature) : true;
    return matchesSearch && matchesStatus && matchesFeature;
  });

  return (
    <div className="fade-in">
      {isDemoMode && (
        <Alert variant="warning" className="rounded-4 mb-4 shadow-sm border-warning">
          <strong>Demo Sandbox:</strong> Showing local sandboxed Applications. Live registrations and deletes are simulated in local React state.
        </Alert>
      )}

      {/* Header controls */}
      <Card className="border-0 shadow-sm rounded-4 mb-4">
        <Card.Body className="p-4">
          <Row className="g-3 align-items-center">
            <Col md={4}>
              <Form.Control
                type="text"
                placeholder="Search applications by name..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="rounded-3"
              />
            </Col>
            <Col md={3}>
              <Form.Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="rounded-3"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                value={filters.feature}
                onChange={(e) => setFilters({ ...filters, feature: e.target.value })}
                className="rounded-3"
              >
                <option value="">All Enabled Features</option>
                {availableFeatures.map(f => (
                  <option key={f.key} value={f.key}>{f.label}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2} className="text-end">
              <Button variant="primary" className="w-100 rounded-3 d-flex align-items-center justify-content-center gap-2 py-2" onClick={() => handleOpenFormModal()}>
                <FaPlus /> Register App
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Applications Grid/Table */}
      <Card className="border-0 shadow-sm rounded-4">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="align-middle mb-0 custom-table">
              <thead className="bg-light text-muted small text-uppercase">
                <tr>
                  <th className="ps-4">Application Details</th>
                  <th>API Authentication Key</th>
                  <th>Daily Limit</th>
                  <th>Allowed Domains</th>
                  <th>Enabled Features</th>
                  <th>Status</th>
                  <th className="text-end pe-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredApps.length > 0 ? (
                  filteredApps.map((app) => {
                    const isVisible = visibleKeys[app._id];
                    const maskedKey = isVisible ? app.apiKey : "ai_••••••••••••••••••••";
                    return (
                      <tr key={app._id}>
                        <td className="ps-4 py-3">
                          <div className="fw-bold text-dark">{app.name}</div>
                          <div className="small text-muted">Created: {new Date(app.createdAt).toLocaleDateString()}</div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2 bg-light px-2 py-1 rounded border" style={{ maxWidth: "280px" }}>
                            <span className="font-monospace text-muted text-truncate" style={{ fontSize: "0.82rem" }}>{maskedKey}</span>
                            <div className="d-flex ms-auto">
                              <Button variant="light" size="sm" className="p-1 border-0" onClick={() => toggleKeyVisibility(app._id)}>
                                {isVisible ? <FaEyeSlash size={13} /> : <FaEye size={13} />}
                              </Button>
                              <Button variant="light" size="sm" className="p-1 border-0" onClick={() => handleCopyKey(app.apiKey)}>
                                <FaCopy size={13} />
                              </Button>
                            </div>
                          </div>
                        </td>
                        <td>
                          <Badge bg="light" className="text-secondary border px-2 py-2 rounded-pill font-monospace" style={{ fontSize: "0.85rem" }}>
                            {app.dailyLimit?.toLocaleString() || "100,000"} / day
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex flex-wrap gap-1" style={{ maxWidth: "200px" }}>
                            {app.allowedDomains?.map((domain, index) => (
                              <Badge key={index} bg="light" className="text-dark border-dashed rounded" style={{ fontSize: "0.75rem" }}>
                                <FaGlobe className="me-1 small" /> {domain}
                              </Badge>
                            ))}
                            {(!app.allowedDomains || app.allowedDomains.length === 0) && (
                              <span className="text-muted small">Any domain (*)</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-wrap gap-1" style={{ maxWidth: "250px" }}>
                            {app.enabledFeatures?.map((featKey) => {
                              const feature = availableFeatures.find(f => f.key === featKey);
                              return (
                                <Badge key={featKey} bg="primary" bg-opacity-10 className="bg-opacity-10 text-primary border" style={{ fontSize: "0.72rem" }}>
                                  {feature ? feature.label : featKey}
                                </Badge>
                              );
                            })}
                          </div>
                        </td>
                        <td>
                          <Badge bg={app.status === "active" ? "success" : "secondary"} className="px-2 py-2 rounded-pill text-capitalize" style={{ fontSize: "0.78rem" }}>
                            {app.status || "Active"}
                          </Badge>
                        </td>
                        <td className="text-end pe-4">
                          <div className="d-flex gap-2 justify-content-end">
                            <Button variant="outline-secondary" size="sm" onClick={() => handleOpenFormModal(app)} title="Edit configuration">
                              <FaEdit size={14} />
                            </Button>
                            <Button variant="outline-danger" size="sm" onClick={() => handleOpenOpenDeleteModal(app)} title="Revoke App Access">
                              <FaTrash size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-5 text-muted">
                      No applications match the search query filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination UI */}
          {serverData?.pagination && serverData.pagination.totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3 px-4 pb-3 border-top pt-3">
              <div className="text-muted small">
                Showing page {serverData.pagination.page} of {serverData.pagination.totalPages} (Total {serverData.pagination.total} records)
              </div>
              <Pagination className="mb-0">
                <Pagination.Prev
                  disabled={serverData.pagination.page === 1}
                  onClick={() => setFilters({ ...filters, page: serverData.pagination.page - 1 })}
                />
                {Array.from({ length: serverData.pagination.totalPages }, (_, index) => {
                  const pageNum = index + 1;
                  return (
                    <Pagination.Item
                      key={pageNum}
                      active={pageNum === serverData.pagination.page}
                      onClick={() => setFilters({ ...filters, page: pageNum })}
                    >
                      {pageNum}
                    </Pagination.Item>
                  );
                })}
                <Pagination.Next
                  disabled={serverData.pagination.page === serverData.pagination.totalPages}
                  onClick={() => setFilters({ ...filters, page: serverData.pagination.page + 1 })}
                />
              </Pagination>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Delete app modal */}
      <DeleteModel
        DELETESTATE={showDeleteModal}
        ONCLICK={handleCloseDeleteModal}
        YES={handleDeleteConfirm}
        DELETETITLE="Revoke App API Key"
        DESCRIPTION={`Are you sure you want to delete "${appToDelete?.name}"? Its API token will be permanently revoked immediately, and downstream clients will experience authentication failures.`}
        loadTest="Revoking..."
      />

      {/* Add / Edit App Modal */}
      <Modal show={showFormModal} onHide={handleCloseFormModal} size="lg" backdrop="static" centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton className="bg-light">
            <Modal.Title className="fw-bold text-dark">
              {currentApp ? `Edit "${currentApp.name}" Config` : "Register New Client Application"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Row className="mb-3">
              <Col md={7}>
                <Form.Group>
                  <Form.Label className="fw-bold text-dark">Application Name</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    placeholder="e.g., iChat Dashboard Widget"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="rounded-3"
                  />
                  <Form.Text className="text-muted">A clear name representing the client app.</Form.Text>
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group>
                  <Form.Label className="fw-bold text-dark">Daily Query Limits</Form.Label>
                  <Form.Control
                    type="number"
                    required
                    min={1}
                    value={formData.dailyLimit}
                    onChange={(e) => setFormData({ ...formData, dailyLimit: e.target.value })}
                    className="rounded-3"
                  />
                  <Form.Text className="text-muted">Hard query request quota limit per calendar day.</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold text-dark">Allowed CORS Domains</Form.Label>
              <Form.Control
                type="text"
                placeholder="https://clientapp.com, https://widget.clientapp.com"
                value={formData.allowedDomains}
                onChange={(e) => setFormData({ ...formData, allowedDomains: e.target.value })}
                className="rounded-3"
              />
              <Form.Text className="text-muted">Comma-separated list of origin domains authorized to issue requests. Leave blank to authorize any domain (*).</Form.Text>
            </Form.Group>

            <Row className="mb-3">
              <Col sm={12}>
                <Form.Label className="fw-bold text-dark d-block">Authorized AI Services</Form.Label>
                <div className="bg-light p-3 border rounded-3 overflow-auto" style={{ maxHeight: "250px" }}>
                  <Row className="g-3">
                    {availableFeatures.map(f => {
                      const isChecked = formData.enabledFeatures.includes(f.key);
                      return (
                        <Col sm={6} md={4} key={f.key}>
                          <Form.Check
                            type="checkbox"
                            id={`feat-${f.key}`}
                            label={f.label}
                            checked={isChecked}
                            onChange={() => handleFeatureToggle(f.key)}
                            className="text-muted fw-semibold"
                          />
                        </Col>
                      );
                    })}
                  </Row>
                </div>
                <Form.Text className="text-muted">Client apps will receive 403 Forbidden errors if they attempt to invoke disabled services.</Form.Text>
              </Col>
            </Row>

            <Form.Group className="mb-2">
              <Form.Check
                type="switch"
                id="app-status-switch"
                label={formData.status === "active" ? "🟢 Status: Active (Authorized)" : "🔴 Status: Inactive (Suspended)"}
                checked={formData.status === "active"}
                onChange={(e) => setFormData({ ...formData, status: e.target.checked ? "active" : "inactive" })}
                className="fw-bold text-dark"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="bg-light border-top">
            <Button variant="secondary" onClick={handleCloseFormModal} className="rounded-3 px-4">
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isRegistering || isUpdating} className="rounded-3 px-4 d-flex align-items-center gap-2">
              {isRegistering || isUpdating ? "Saving Config..." : <><FaCheck /> Save Configuration</>}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );

  function handleOpenOpenDeleteModal(app) {
    handleOpenDeleteModal(app);
  }
};

export default AIApplications;
